import { SignInScreen } from "./SignInScreen";
import { PageErrorBoundary } from "./PageErrorBoundary";
import { StudentDetail } from "./portal/StudentDetail";
import { clearAccountDrafts } from "./draft-storage";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ResultsScreen } from "./Results";
import { api, ApiError } from "./api";
import { AttemptRunner } from "./AttemptRunner";
import { authClient } from "./auth-client";
import { PortalShell, PageHeading } from "./PortalShell";
import { AdminTests, AdminAssignments, AdminStudents, AdminTestPreview, StudentTests } from "./PortalPages";
import { Link, navigate, useLocation } from "./navigation";
import { clearPortalCache, type Actor } from "./portal-data";
import type { SectionScope } from "./section-scope";
import type { Timing } from "../../../packages/question-presentation/timing";

const StudentProgress = lazy(() => import("./analytics/StudentProgress").then((module) => ({ default: module.StudentProgress })));
const TeacherReviews = lazy(() => import("./analytics/TeacherReviews").then((module) => ({ default: module.TeacherReviews })));
const StudyWorkspace = lazy(() => import("./study/StudyWorkspace").then((module) => ({ default: module.StudyWorkspace })));
const TeacherOverview = lazy(() => import("./analytics/TeacherOverview").then((module) => ({ default: module.TeacherOverview })));

export function App() {
  const [actor, setActor] = useState<Actor | null>();
  const [localPreview, setLocalPreview] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const hash = location.hash;
  const lock = useRef(false);
  const requests = useRef(new Map<string, string>());
  const path = location.pathname;
  useEffect(() => {
    const controller = new AbortController();
    void api<{ actor: Actor; localPreview: boolean }>("/api/me", { signal: controller.signal }).then((value) => {
      if (controller.signal.aborted) return;
      const admin = value.actor.role !== "learner";
      const prefix = admin ? "/admin/" : "/student/";
      if (!window.location.pathname.startsWith(prefix)) navigate(`${prefix}${admin ? "overview" : "tests"}${window.location.hash}`, true);
      setActor(value.actor); setLocalPreview(value.localPreview);
    }).catch((reason) => { if (controller.signal.aborted) return; if (reason instanceof ApiError && (reason.status === 401 || reason.status === 403)) { setActor(null); if (reason.status === 403) setError("Access has not been approved. Ask your teacher to add this Google email."); } else setError(reason.message); });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    const heading = document.querySelector<HTMLElement>(".portal-heading h1");
    heading?.focus({ preventScroll: true });
  }, [path]);
  const showResults = useCallback(async (id: string) => { navigate(`#results=${encodeURIComponent(id)}`); }, []);
  const resume = (id: string) => { navigate(`#attempt=${encodeURIComponent(id)}`); };
  const exit = () => { navigate(window.location.pathname + window.location.search); };
  async function start(exam: { id: string }, assignmentId?: string, options?: { timing: Timing; section: SectionScope }) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError("");
    const key = assignmentId ?? exam.id;
    const requestId = requests.current.get(key) ?? crypto.randomUUID(); requests.current.set(key, requestId);
    try { const result = await api<{ id: string }>("/api/attempts", { method: "POST", body: JSON.stringify({ examId: exam.id, assignmentId, mode: "fixed", requestId, ...(!assignmentId && options ? options : {}) }) }); requests.current.delete(key); resume(result.id); }
    catch (reason) { setError((reason as Error).message); }
    finally { lock.current = false; setBusy(false); }
  }
  async function signIn() {
    if (busy) return;
    setBusy(true); setError("");
    try { const result = await authClient.signIn.social({ provider: "google", callbackURL: "/", errorCallbackURL: "/sign-in" }); if (result.error) setError(result.error.message ?? "Sign-in failed."); }
    catch { setError("Could not connect. Please try again."); }
    finally { setBusy(false); }
  }
  async function signOut() {
    try { const result = await authClient.signOut(); if (result.error) throw new Error(result.error.message ?? "Sign-out failed."); clearPortalCache(); if (actor) await clearAccountDrafts(actor.id); window.location.href = "/"; }
    catch (reason) { setError((reason as Error).message); }
  }
  if (actor === undefined) return <main className="center"><div>{error ? <><p className="error" role="alert">{error}</p><button onClick={() => window.location.reload()}>Retry</button><p><a href="/">Return to sign-in</a></p></> : <div className="loading-line" role="status" aria-label="Loading" />}</div></main>;
  const authError = location.searchParams.get("error");
  if (!actor) return <SignInScreen authError={authError} error={error} busy={busy} onSignIn={() => void signIn()} />;
  const params = new URLSearchParams(hash.slice(1));
  const results = params.get("results"), attempt = params.get("attempt");
  if (results) return <ResultsScreen id={results} key={results} admin={actor.role !== "learner" && path.startsWith("/admin/")} onExit={exit} />;
  if (attempt) return <AttemptRunner userId={actor.id} id={attempt} key={attempt} onExit={exit} onCompleted={showResults} />;
  const admin = actor.role !== "learner";
  let active = path.startsWith("/admin/tests/") ? "/admin/tests" : path.startsWith("/admin/students/") ? "/admin/students" : path;
  let page;
  if (admin) {
    if (path === "/admin/overview") page = <Suspense fallback={<div className="loading-line" role="status" aria-label="Loading overview" />}><TeacherOverview onResults={showResults} /></Suspense>;
    else if (path === "/admin/reviews") page = <Suspense fallback={<div className="loading-line" role="status" aria-label="Loading reviews" />}><TeacherReviews onResults={showResults} /></Suspense>;
    else if (path === "/admin/assignments") page = <AdminAssignments onResults={showResults} />;
    else if (path.startsWith("/admin/students/")) page = <StudentDetail key={path} id={decodeRouteSegment(path.slice("/admin/students/".length))} onResults={showResults} />;
    else if (path === "/admin/students") page = <AdminStudents canManage={actor.role === "owner"} />;
    else if (path.startsWith("/admin/tests/")) page = <AdminTestPreview key={path} examId={decodeRouteSegment(path.slice("/admin/tests/".length))} />;
    else if (path === "/admin/tests") page = <AdminTests />;
  } else {
    if (path === "/student/progress") page = <Suspense fallback={<div className="loading-line" role="status" aria-label="Loading progress" />}><StudentProgress onResults={showResults} /></Suspense>;
    else if (path === "/student/study") page = <Suspense fallback={<div className="loading-line" role="status" aria-label="Loading study desk" />}><StudyWorkspace /></Suspense>;
    else if (["/student/tests", "/student/plan", "/student/history"].includes(path)) { active = "/student/tests"; page = <StudentTests
      onStart={(exam, assignmentId, options) => void start(exam, assignmentId, options)}
      onResume={resume} onResults={showResults} busy={busy} error={error}
    />; }
  }
  return <PortalShell actor={actor} localPreview={localPreview} active={active} onSignOut={() => void signOut()}>
    {admin && error && <p className="error" role="alert">{error}</p>}
    <PageErrorBoundary key={path}>{page ?? <><PageHeading eyebrow="Not found" title="Page unavailable" /><div className="portal-content"><Link href={admin ? "/admin/tests" : "/student/tests"}>Back to tests</Link></div></>}</PageErrorBoundary>
  </PortalShell>;
}



function decodeRouteSegment(value: string) {
  try { return decodeURIComponent(value); } catch { return value; }
}
